import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { mainSiteUrl, categories } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    document.documentElement.lang = code;
    setLangOpen(false);
    setMenuOpen(false);
  };

  const goHome = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goSection = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    if (window.location.pathname === '/') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  };

  const currentLang = languages.find((l) => l.code === i18n.language)?.label ?? 'English';

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <a
            href="/"
            onClick={goHome}
            className="flex items-center gap-2 cursor-pointer"
          >
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-grid-fill text-base"></i>
            </span>
            <span className="font-heading font-bold text-lg text-foreground-950">USFansLink</span>
          </a>

          <nav className="hidden lg:flex items-center gap-6">
            <a
              href="/"
              onClick={goHome}
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t('nav.home')}
            </a>
            <a
              href="#categories"
              onClick={goSection('categories')}
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t('nav.categories')}
            </a>
            <a
              href="#guides"
              onClick={goSection('guides')}
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t('nav.guides')}
            </a>
            <a
              href="#safety"
              onClick={goSection('safety')}
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t('nav.safety')}
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t('nav.shoppingGuide')}
            </a>
          </nav>

          <div className="flex items-center gap-2">
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-global-line text-base"></i>
                <span className="hidden sm:inline">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-1 w-40 bg-background-50 border border-background-200 rounded-lg py-1">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => changeLang(l.code)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 whitespace-nowrap cursor-pointer ${
                        i18n.language === l.code ? 'text-primary-600 font-semibold' : 'text-foreground-800'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t('nav.browse')}
            </a>

            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-900 hover:bg-background-100 cursor-pointer"
              aria-label="Menu"
            >
              <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="lg:hidden fixed inset-x-0 top-16 bottom-0 z-[60] bg-background-50 border-t border-background-200 overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            <a
              href="/"
              onClick={goHome}
              className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-home-4-line text-lg"></i>
              {t('nav.home')}
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-shopping-bag-3-line text-lg"></i>
              {t('nav.shoppingGuide')}
            </a>

            <div className="rounded-md">
              <button
                onClick={() => setCatOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg"></i>
                  {t('nav.categories')}
                </span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {catOpen && (
                <div className="pl-4 pb-1 grid grid-cols-1 gap-0.5">
                  {categories.map((c) => (
                    <a
                      key={c.key}
                      href={c.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 overflow-hidden shrink-0">
                        <img src={c.image} alt={t(c.key)} className="w-full h-full object-cover object-top" />
                      </span>
                      <span className="truncate">{t(c.key)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="#guides"
              onClick={goSection('guides')}
              className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-book-2-line text-lg"></i>
              {t('nav.guides')}
            </a>
            <a
              href="#safety"
              onClick={goSection('safety')}
              className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-shield-check-line text-lg"></i>
              {t('nav.safety')}
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer mt-1 mb-8"
            >
              {t('nav.browse')}
            </a>
          </div>
        </div>
      )}
    </>
  );
}